import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  User,
  BarChart2,
  MessageSquare,
  Briefcase,
  Eye,
  Link as LinkIcon,
  Phone,
  Mail,
  MapPin,
  RefreshCw,
  AlertCircle,
  Users,
  TrendingUp,
  MousePointerClick,
  QrCode,
  Globe,
  Download,
  Search,
  ExternalLink,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from 'lucide-react';
import { viewOwnerDashboard } from '../../services/userAdmin';
import type {
  ViewOwnerDashboardResponse,
  TeamPermissions,
  OwnerProfileData,
  OwnerAnalytics,
  OwnerLead,
  OwnerCollaboration,
} from '../../services/userAdmin';

// ---------------------------------------------------------------------------
// Lead status — localStorage-based (same keys as Leads.tsx so statuses sync)
// ---------------------------------------------------------------------------
type LeadStatus = 'contacted' | 'follow_up' | 'converted' | 'lost';

const getLeadStatus = (leadId: number): LeadStatus | undefined =>
  (localStorage.getItem(`lead_status_${leadId}`) as LeadStatus) || undefined;

const saveLeadStatus = (leadId: number, status: LeadStatus | '') => {
  if (status === '') {
    localStorage.removeItem(`lead_status_${leadId}`);
  } else {
    localStorage.setItem(`lead_status_${leadId}`, status);
  }
};

interface StatusCfg {
  label: string;
  badge: string;
  dot: string;
}

const STATUS_CFG: Record<LeadStatus, StatusCfg> = {
  contacted: {
    label: 'Contacted',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
  },
  follow_up: {
    label: 'Follow Up',
    badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    dot: 'bg-yellow-500',
  },
  converted: {
    label: 'Converted',
    badge: 'bg-green-100 text-green-700 border-green-200',
    dot: 'bg-green-500',
  },
  lost: {
    label: 'Lost',
    badge: 'bg-red-100 text-red-700 border-red-200',
    dot: 'bg-red-500',
  },
};

const NEW_CFG: StatusCfg = {
  label: 'New',
  badge: 'bg-gray-100 text-gray-600 border-gray-200',
  dot: 'bg-gray-400',
};

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------
function csvEscape(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

function buildCsvBlob(leads: OwnerLead[]): Blob {
  const headers = [
    'Name', 'Email', 'Mobile', 'Subject', 'Message',
    'Status', 'Platform', 'Location', 'Date',
  ];
  const rows = leads.map((l) => {
    const s = getLeadStatus(l.id);
    return [
      l.name,
      l.email ?? '',
      l.mobile_number,
      l.subject,
      l.message,
      s ? STATUS_CFG[s].label : 'New',
      l.platform ?? '',
      l.location ?? '',
      fmtDate(l.created_at),
    ].map((v) => csvEscape(String(v)));
  });
  const content = [headers, ...rows].map((r) => r.join(',')).join('\n');
  return new Blob([content], { type: 'text/csv;charset=utf-8;' });
}

// ---------------------------------------------------------------------------
// General helpers
// ---------------------------------------------------------------------------
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </p>
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Collaboration detail modal
// ---------------------------------------------------------------------------
const CollabModal: React.FC<{
  collab: OwnerCollaboration;
  onClose: () => void;
}> = ({ collab, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Modal header */}
      <div className="relative bg-primary-50 p-6 flex flex-col items-center text-center">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/60 transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
        {collab.logo ? (
          <img
            src={collab.logo}
            alt={collab.name}
            className="h-20 w-20 rounded-xl object-cover border-4 border-white shadow mb-3"
          />
        ) : (
          <div className="h-20 w-20 rounded-xl bg-primary-100 flex items-center justify-center border-4 border-white shadow mb-3">
            <Briefcase className="h-8 w-8 text-primary-400" />
          </div>
        )}
        <h2 className="text-lg font-bold text-gray-900">{collab.name}</h2>
      </div>

      {/* Modal body */}
      <div className="p-5 space-y-4">
        {collab.description ? (
          <p className="text-sm text-gray-600 leading-relaxed">
            {collab.description}
          </p>
        ) : (
          <p className="text-sm text-gray-400 text-center italic">
            No description provided.
          </p>
        )}
        {collab.url ? (
          <a
            href={collab.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Visit Website
          </a>
        ) : (
          <p className="text-xs text-gray-400 text-center">
            No website link available.
          </p>
        )}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Tab: Profile
// ---------------------------------------------------------------------------
const ProfileTab: React.FC<{ profile: OwnerProfileData }> = ({ profile }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const url = `${window.location.origin}/${profile.profile_url}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        toast.success('Profile URL copied!');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error('Failed to copy URL'));
  };

  return (
    <div className="space-y-5">
      {/* Basic info card */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <User className="h-4 w-4 text-primary-600" />
            Basic Information
          </h3>
          {profile.profile_url && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                title="Copy profile URL"
                className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-primary-600 hover:border-primary-300 transition-colors"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
              <a
                href={`/${profile.profile_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary-200 text-primary-600 text-xs font-medium hover:bg-primary-50 transition-colors"
              >
                <Globe className="h-3.5 w-3.5" />
                View Profile
              </a>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {profile.name && (
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Name</p>
              <p className="text-sm font-medium text-gray-800">{profile.name}</p>
            </div>
          )}
          {profile.tagline && (
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Tagline</p>
              <p className="text-sm text-gray-700">{profile.tagline}</p>
            </div>
          )}
          {profile.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                <p className="text-sm text-gray-700">{profile.phone}</p>
              </div>
            </div>
          )}
          {profile.secondary_phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Secondary Phone</p>
                <p className="text-sm text-gray-700">{profile.secondary_phone}</p>
              </div>
            </div>
          )}
          {profile.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Email</p>
                <p className="text-sm text-gray-700">{profile.email}</p>
              </div>
            </div>
          )}
          {profile.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Location</p>
                <p className="text-sm text-gray-700">{profile.location}</p>
              </div>
            </div>
          )}
        </div>

        {profile.bio && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-1">Bio</p>
            <p className="text-sm text-gray-700 leading-relaxed">{profile.bio}</p>
          </div>
        )}
      </div>

      {/* Links card */}
      {(profile.social_links?.length > 0 || profile.custom_links?.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-primary-600" />
            Links
            <span className="ml-auto text-xs font-normal text-gray-400">
              {(profile.social_links?.length ?? 0) +
                (profile.custom_links?.length ?? 0)}{' '}
              total
            </span>
          </h3>
          <div className="space-y-2">
            {[
              ...(profile.social_links ?? []).map((l) => ({
                id: `s-${l.id}`,
                label: l.label ?? l.platform,
                url: l.url,
              })),
              ...(profile.custom_links ?? []).map((l) => ({
                id: `c-${l.id}`,
                label: l.label,
                url: l.url,
              })),
            ].map((link) => (
              <div
                key={link.id}
                className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">
                    {link.label}
                  </span>
                </div>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:underline flex-shrink-0 flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab: Analytics
// ---------------------------------------------------------------------------
const AnalyticsTab: React.FC<{ analytics: OwnerAnalytics }> = ({ analytics }) => {
  const ctr =
    analytics.total_views > 0
      ? ((analytics.total_clicks / analytics.total_views) * 100).toFixed(1)
      : '0.0';

  const barPct = (part: number, total: number) =>
    total > 0 ? Math.min((part / total) * 100, 100) : 0;

  if (analytics.total_views === 0) {
    return (
      <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
        <BarChart2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No views yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Analytics will appear once the profile gets traffic.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Total Views"
          value={analytics.total_views.toLocaleString()}
          icon={<Eye className="h-4 w-4 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label="QR Scans"
          value={analytics.qr_views.toLocaleString()}
          icon={<QrCode className="h-4 w-4 text-purple-600" />}
          color="bg-purple-50"
        />
        <StatCard
          label="Direct Views"
          value={analytics.direct_views.toLocaleString()}
          icon={<TrendingUp className="h-4 w-4 text-green-600" />}
          color="bg-green-50"
        />
        <StatCard
          label="Link Clicks"
          value={analytics.total_clicks.toLocaleString()}
          icon={<MousePointerClick className="h-4 w-4 text-orange-600" />}
          color="bg-orange-50"
        />
      </div>

      {/* CTR banner */}
      <div className="bg-gradient-to-r from-primary-50 to-indigo-50 border border-primary-100 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-0.5">
            Click-Through Rate
          </p>
          <p className="text-3xl font-bold text-primary-700">{ctr}%</p>
          <p className="text-xs text-primary-500 mt-0.5">of visitors clicked a link</p>
        </div>
        <div className="p-3 bg-primary-100 rounded-xl">
          <TrendingUp className="h-6 w-6 text-primary-600" />
        </div>
      </div>

      {/* Traffic breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Traffic Breakdown</h3>
        <div className="space-y-4">
          {/* QR */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-purple-400" />
                QR Scans
              </span>
              <span className="font-medium text-gray-700">
                {analytics.qr_views.toLocaleString()}&nbsp;
                ({Math.round(barPct(analytics.qr_views, analytics.total_views))}%)
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-400 rounded-full transition-all duration-700"
                style={{ width: `${barPct(analytics.qr_views, analytics.total_views)}%` }}
              />
            </div>
          </div>
          {/* Direct */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
                Direct Views
              </span>
              <span className="font-medium text-gray-700">
                {analytics.direct_views.toLocaleString()}&nbsp;
                ({Math.round(barPct(analytics.direct_views, analytics.total_views))}%)
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-400 rounded-full transition-all duration-700"
                style={{ width: `${barPct(analytics.direct_views, analytics.total_views)}%` }}
              />
            </div>
          </div>
          {/* Clicks */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-orange-400" />
                Link Clicks
              </span>
              <span className="font-medium text-gray-700">
                {analytics.total_clicks.toLocaleString()}&nbsp;({ctr}% CTR)
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-400 rounded-full transition-all duration-700"
                style={{ width: `${barPct(analytics.total_clicks, analytics.total_views)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lead status dropdown (inline, localStorage-backed)
// ---------------------------------------------------------------------------
const LeadStatusDropdown: React.FC<{
  leadId: number;
  onChanged: () => void;
}> = ({ leadId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const current = getLeadStatus(leadId);
  const cfg: StatusCfg = current ? STATUS_CFG[current] : NEW_CFG;

  const handleSelect = (val: LeadStatus | '') => {
    saveLeadStatus(leadId, val);
    setOpen(false);
    onChanged();
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${cfg.badge}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
        {cfg.label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[140px]">
            <button
              onClick={() => handleSelect('')}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors text-gray-600"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
              New
              {!current && <Check className="h-3 w-3 ml-auto text-primary-600" />}
            </button>
            {(Object.keys(STATUS_CFG) as LeadStatus[]).map((s) => (
              <button
                key={s}
                onClick={() => handleSelect(s)}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors text-gray-700"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${STATUS_CFG[s].dot}`} />
                {STATUS_CFG[s].label}
                {current === s && (
                  <Check className="h-3 w-3 ml-auto text-primary-600" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab: Leads (search, filter, status, export, pagination)
// ---------------------------------------------------------------------------
const PAGE_SIZE = 10;

const LeadsTab: React.FC<{
  leads: OwnerLead[];
  ownerName: string;
}> = ({ leads, ownerName }) => {
  const [query, setQuery] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0); // triggers re-render after status change

  const uniqueSubjects = Array.from(
    new Set(leads.map((l) => l.subject).filter(Boolean))
  );

  const filtered = leads.filter((l) => {
    const q = query.toLowerCase().trim();
    const matchesQuery =
      !q ||
      l.name.toLowerCase().includes(q) ||
      (l.email ?? '').toLowerCase().includes(q) ||
      l.mobile_number.includes(q) ||
      l.subject.toLowerCase().includes(q) ||
      l.message.toLowerCase().includes(q);

    const matchesSubject = !subjectFilter || l.subject === subjectFilter;

    const s = getLeadStatus(l.id);
    const sLabel = s ? STATUS_CFG[s].label.toLowerCase() : 'new';
    const matchesStatus = !statusFilter || sLabel === statusFilter.toLowerCase();

    return matchesQuery && matchesSubject && matchesStatus;
  });

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = page * PAGE_SIZE < filtered.length;
  const unread = leads.filter((l) => !l.is_read).length;

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [query, subjectFilter, statusFilter, tick]);

  const handleExport = () => {
    if (leads.length === 0) {
      toast.error('No leads to export');
      return;
    }
    const source = filtered.length < leads.length ? filtered : leads;
    const blob = buildCsvBlob(source);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${ownerName.replace(/\s+/g, '_')}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${source.length} lead${source.length !== 1 ? 's' : ''}`);
  };

  if (leads.length === 0) {
    return (
      <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
        <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No leads yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Contact form submissions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary + export row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-gray-700">
            {leads.length} lead{leads.length !== 1 ? 's' : ''}
          </p>
          {unread > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
              {unread} unread
            </span>
          )}
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Download className="h-3.5 w-3.5 text-gray-500" />
          Export CSV
        </button>
      </div>

      {/* Search + filters row */}
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Subject filter */}
        {uniqueSubjects.length > 1 && (
          <select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          >
            <option value="">All subjects</option>
            {uniqueSubjects.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
        >
          <option value="">All statuses</option>
          <option value="new">New</option>
          {(Object.keys(STATUS_CFG) as LeadStatus[]).map((s) => (
            <option key={s} value={STATUS_CFG[s].label.toLowerCase()}>
              {STATUS_CFG[s].label}
            </option>
          ))}
        </select>
      </div>

      {/* Empty filtered state */}
      {filtered.length === 0 && (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl">
          <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 font-medium">No leads match your filters</p>
          <button
            onClick={() => { setQuery(''); setSubjectFilter(''); setStatusFilter(''); }}
            className="mt-2 text-xs text-primary-600 hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Lead cards */}
      <div className="space-y-3">
        {paginated.map((lead) => (
          <div key={lead.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900">{lead.name}</p>
                  {!lead.is_read && (
                    <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                      New
                    </span>
                  )}
                </div>
                {lead.email && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {lead.email}
                  </p>
                )}
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <Phone className="h-3 w-3" />
                  {lead.mobile_number}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <p className="text-xs text-gray-400">{fmtDate(lead.created_at)}</p>
                <LeadStatusDropdown leadId={lead.id} onChanged={() => setTick(t => t + 1)} />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-600 mb-0.5">{lead.subject}</p>
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">{lead.message}</p>
            </div>
            {(lead.platform || lead.location) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {lead.platform && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    {lead.platform}
                  </span>
                )}
                {lead.location && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {lead.location}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Show more / less */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex justify-center">
          {hasMore ? (
            <button
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ChevronDown className="h-4 w-4" />
              Show more ({filtered.length - page * PAGE_SIZE} remaining)
            </button>
          ) : (
            <button
              onClick={() => setPage(1)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ChevronUp className="h-4 w-4" />
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab: Collaborations (clickable cards open detail modal)
// ---------------------------------------------------------------------------
const CollaborationsTab: React.FC<{ collaborations: OwnerCollaboration[] }> = ({ collaborations }) => {
  const [selected, setSelected] = useState<OwnerCollaboration | null>(null);

  if (collaborations.length === 0) {
    return (
      <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
        <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No collaborations yet</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-400 mb-3">Tap a card to see full details</p>
      <div className="space-y-3">
        {collaborations.map((collab) => (
          <button
            key={collab.id}
            onClick={() => setSelected(collab)}
            className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-4 hover:border-primary-300 hover:shadow-md transition-all group"
          >
            {collab.logo ? (
              <img
                src={collab.logo}
                alt={collab.name}
                className="h-12 w-12 rounded-lg object-cover flex-shrink-0 border border-gray-100"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-5 w-5 text-primary-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
                {collab.name}
              </p>
              {collab.description && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{collab.description}</p>
              )}
              {collab.url && (
                <p className="text-xs text-primary-500 mt-0.5 truncate">{collab.url}</p>
              )}
            </div>
            <ExternalLink className="h-4 w-4 text-gray-300 group-hover:text-primary-400 flex-shrink-0 transition-colors" />
          </button>
        ))}
      </div>

      {selected && <CollabModal collab={selected} onClose={() => setSelected(null)} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
type TabKey = 'profile' | 'analytics' | 'leads' | 'collaborations';

const TAB_DEFS: {
  key: TabKey;
  label: string;
  icon: React.FC<{ className?: string }>;
}[] = [
  { key: 'profile',        label: 'Profile',        icon: ({ className }) => <User          className={className} /> },
  { key: 'analytics',      label: 'Analytics',      icon: ({ className }) => <BarChart2     className={className} /> },
  { key: 'leads',          label: 'Leads',          icon: ({ className }) => <MessageSquare className={className} /> },
  { key: 'collaborations', label: 'Collabs',        icon: ({ className }) => <Briefcase     className={className} /> },
];

// ---------------------------------------------------------------------------
// Main TeamView component
// ---------------------------------------------------------------------------
const TeamView: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const navigate = useNavigate();

  const [data, setData]         = useState<ViewOwnerDashboardResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('profile');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!ownerId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = await viewOwnerDashboard(Number(ownerId));
      if (res.data.success) {
        setData(res.data);
        if (!silent) {
          const perms: TeamPermissions = res.data.permissions;
          const first = TAB_DEFS.find(t => perms[t.key]);
          if (first) setActiveTab(first.key);
        }
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'You do not have access to this dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [ownerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const permissions: TeamPermissions = data?.permissions ?? {
    profile: false, analytics: false, leads: false, collaborations: false,
  };

  const availableTabs = TAB_DEFS.filter(t => permissions[t.key]);
  const unreadLeads   = data?.data?.leads?.filter(l => !l.is_read).length ?? 0;
  const profileUrl    = data?.data?.profile?.profile_url ?? null;

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard/useradmin')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Team Access
        </button>
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => navigate('/dashboard/useradmin')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Team Access
        </button>
        <div className="flex items-center gap-2">
          {profileUrl && (
            <a
              href={`/${profileUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              View Profile
            </a>
          )}
          <button
            onClick={() => fetchData(true)}
            title="Refresh"
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Owner header card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm mb-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-bold text-primary-700">
              {data.owner.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-gray-900">{data.owner.name}</h1>
              <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium border border-primary-200">
                Team Member View
              </span>
            </div>
            <p className="text-sm text-gray-500">{data.owner.email}</p>
          </div>
          <Users className="h-5 w-5 text-gray-300 flex-shrink-0" />
        </div>

        {/* Granted permissions chips */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1.5">
          {TAB_DEFS.filter(t => permissions[t.key]).map(t => (
            <span key={t.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <t.icon className="h-3 w-3" />
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {/* No permissions */}
      {availableTabs.length === 0 && (
        <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
          <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No sections accessible</p>
          <p className="text-sm text-gray-400 mt-1">
            Ask the owner to grant you at least one permission.
          </p>
        </div>
      )}

      {/* Tab bar + content */}
      {availableTabs.length > 0 && (
        <>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 overflow-x-auto">
            {availableTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-max flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {/* Unread badge on leads tab */}
                {tab.key === 'leads' && unreadLeads > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-xs font-bold leading-none">
                    {unreadLeads}
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'profile'        && data.data.profile        && <ProfileTab        profile={data.data.profile} />}
          {activeTab === 'analytics'      && data.data.analytics      && <AnalyticsTab      analytics={data.data.analytics} />}
          {activeTab === 'leads'          && data.data.leads          && <LeadsTab          leads={data.data.leads} ownerName={data.owner.name} />}
          {activeTab === 'collaborations' && data.data.collaborations && <CollaborationsTab collaborations={data.data.collaborations} />}
        </>
      )}
    </div>
  );
};

export default TeamView;
